import { useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  HelpCircle, Loader2, Calculator, History as HistoryIcon,
  ChevronDown, ChevronUp, PlayCircle, AlertTriangle, Info, Sparkles, ArrowDownCircle,
} from "lucide-react";

const AREA_LABEL = {
  caja: "Caja", cocina: "Cocina", gerente_sucursal: "Gerente Sucursal",
  operaciones: "Operaciones", rh: "RH y Nominas", compras: "Compras",
  direccion: "Direccion", legal: "Legal", tesoreria: "Tesoreria",
  diseno: "Diseno", otro: "Otro",
};
const AREA_COLOR = {
  caja: "bg-blue-50 text-blue-700 border-blue-200",
  cocina: "bg-orange-50 text-orange-700 border-orange-200",
  gerente_sucursal: "bg-purple-50 text-purple-700 border-purple-200",
  operaciones: "bg-teal-50 text-teal-700 border-teal-200",
  rh: "bg-pink-50 text-pink-700 border-pink-200",
  compras: "bg-emerald-50 text-emerald-700 border-emerald-200",
  direccion: "bg-slate-50 text-slate-700 border-slate-300",
  legal: "bg-amber-50 text-amber-700 border-amber-200",
  tesoreria: "bg-blue-50 text-blue-700 border-blue-200",
  diseno: "bg-indigo-50 text-indigo-700 border-indigo-200",
  otro: "bg-slate-50 text-slate-500 border-slate-200",
};
const FREQ_PERIOD_LABEL = { diaria: "Captura diaria", semanal: "Captura semanal", mensual: "Captura mensual" };

// Evaluador local liviano para preview en vivo (la fórmula viene de config admin, ya validada en backend)
function computeLocal(formula, values) {
  try {
    const keys = Object.keys(values);
    const args = keys.map((k) => Number(values[k]));
    if (args.length === 0 || args.some((a) => Number.isNaN(a))) return null;
    const helpers =
      "const min=Math.min,max=Math.max,abs=Math.abs,round=(x,d=0)=>{const p=Math.pow(10,d);return Math.round(x*p)/p;};";
    // eslint-disable-next-line no-new-func
    const fn = new Function(...keys, `${helpers} return (${formula});`);
    const r = fn(...args);
    if (typeof r !== "number" || !Number.isFinite(r)) return null;
    return r;
  } catch {
    return null;
  }
}

function formatOutput(val, unit, decimals) {
  if (val === null || val === undefined) return "—";
  const d = Number.isInteger(decimals) ? decimals : 2;
  const n = Number(val).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: d });
  if (unit === "percentage") return `${n}%`;
  if (unit === "currency") return `MX$${n}`;
  if (unit === "count") return n;
  return n;
}

// Nivel verde/amarillo/rojo según el rango aceptable (espeja compute_result_level del backend)
function computeRangeLevel(value, min, max) {
  if (value === null || value === undefined || min === null || min === undefined || max === null || max === undefined) return null;
  if (value >= min && value <= max) return "verde";
  const span = Math.max(max - min, 1e-9);
  const tolerance = span * 0.2;
  if (value >= min - tolerance && value <= max + tolerance) return "amarillo";
  return "rojo";
}

const RANGE_LEVEL_CLS = {
  verde: "bg-emerald-50 border-emerald-200 text-emerald-700",
  amarillo: "bg-yellow-50 border-yellow-200 text-yellow-700",
  rojo: "bg-red-50 border-red-200 text-red-700",
};

function Sparkline({ values }) {
  if (!values || values.length < 2) return null;
  const w = 120, h = 28, pad = 2;
  const min = Math.min(...values), max = Math.max(...values);
  const span = max - min || 1;
  const pts = values
    .map((v, i) => {
      const x = pad + (i * (w - 2 * pad)) / (values.length - 1);
      const y = h - pad - ((v - min) / span) * (h - 2 * pad);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible" data-testid="kpi-sparkline">
      <polyline points={pts} fill="none" stroke="#FFB200" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export default function KpiCaptureCard({ kpi, onSaved, userRole, readOnly = false }) {
  const hasFormula = !!kpi.formula_enabled;
  const inputDefs = kpi.input_fields || [];
  const needsBranchSelection = !!kpi.needs_branch_selection;

  const [inputs, setInputs] = useState({});
  const [manualValue, setManualValue] = useState("");
  const [notes, setNotes] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const tid = kpi.kpi_definition_id;

  const calculated = useMemo(() => {
    if (!hasFormula) return null;
    const filled = {};
    for (const f of inputDefs) {
      const raw = inputs[f.key];
      if (raw === undefined || raw === "") return null; // requiere todos los campos para preview
      filled[f.key] = raw;
    }
    return computeLocal(kpi.formula_template, filled);
  }, [hasFormula, inputs, inputDefs, kpi.formula_template]);

  const rangeLevel = useMemo(() => {
    if (calculated === null) return null;
    return computeRangeLevel(calculated, kpi.acceptable_min, kpi.acceptable_max);
  }, [calculated, kpi]);

  const loadHistory = useCallback(async () => {
    try {
      const params = new URLSearchParams({ kpi_id: tid });
      if (kpi.branch_id) params.set("branch_id", kpi.branch_id);
      const res = await api.get(`/ops/kpis/records?${params}`);
      setHistory((res.data || []).slice(0, 6));
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoaded(true);
    }
  }, [tid, kpi.branch_id]);

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && !historyLoaded) loadHistory();
  };

  const setInput = (key, val) => setInputs((p) => ({ ...p, [key]: val }));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        kpi_definition_id: tid,
        branch_id: kpi.branch_id || undefined,
        notes: notes.trim() || undefined,
      };
      if (kpi.capture_period) payload.period = kpi.capture_period;
      if (hasFormula) {
        const raw = {};
        for (const f of inputDefs) {
          const v = inputs[f.key];
          if (v === undefined || v === "") {
            if (f.required !== false) {
              toast.error(`Falta el campo: ${f.label}`);
              setSaving(false);
              return;
            }
            continue;
          }
          raw[f.key] = Number(v);
        }
        payload.inputs_raw = raw;
      } else {
        const num = parseFloat(manualValue);
        if (Number.isNaN(num)) {
          toast.error("Ingresa un valor numérico");
          setSaving(false);
          return;
        }
        payload.value = num;
      }
      const res = await api.post("/ops/kpis/records", payload);
      if (res.data?.warning) toast.warning(res.data.warning);
      else toast.success("KPI capturado correctamente");
      window.dispatchEvent(new CustomEvent("kpi-updated"));
      onSaved(tid, kpi.branch_id);
    } catch (e) {
      // Fix WH91 (2026-10-31): antes un 5xx o un detalle que no era texto se veia como
      // "Error al capturar KPI" a secas, y tras un 409 la tarjeta seguia como pendiente.
      const st = e.response?.status;
      const det = e.response?.data?.detail;
      const txt = typeof det === "string" ? det
        : Array.isArray(det) ? det.map((x) => x?.msg).filter(Boolean).join(" · ") : "";
      toast.error(txt ? `${txt} (${st})` : `Error al capturar KPI (${st ? `HTTP ${st}` : "sin respuesta del servidor"})`);
      if (st === 409) onSaved(tid, kpi.branch_id); // ya existe: recargar para verlo como capturado
    } finally {

      setSaving(false);
    }
  };

  const canSubmit = hasFormula
    ? inputDefs.filter((f) => f.required !== false).every((f) => inputs[f.key] !== undefined && inputs[f.key] !== "")
    : manualValue !== "";

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 space-y-3" data-testid={`kpi-card-${tid}`}>
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${AREA_COLOR[kpi.area] || AREA_COLOR.otro}`}>
              {AREA_LABEL[kpi.area] || kpi.area}
            </span>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid={`kpi-period-badge-${tid}`}>
              {FREQ_PERIOD_LABEL[kpi.frequency] || kpi.frequency}
            </span>
            {kpi.branch_name && <span className="text-xs text-slate-500 font-medium">{kpi.branch_name}</span>}
            {hasFormula ? (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                <Calculator className="w-3 h-3" /> Cálculo automático
              </span>
            ) : null}
          </div>
          <div className="font-bold text-slate-900 text-base leading-snug flex items-center gap-1.5">
            {kpi.name}
            {kpi.score_direction === "lower_is_better" && (
              <ArrowDownCircle className="w-3.5 h-3.5 text-blue-500 shrink-0" title="Menor es mejor" data-testid={`kpi-lower-icon-${tid}`} />
            )}
          </div>
        </div>
        <button
          onClick={() => setShowHelp((p) => !p)}
          className="shrink-0 p-2 rounded-full border border-slate-200 hover:bg-slate-50 text-slate-500 transition-colors"
          title="¿Qué es este KPI y cómo se captura?"
          data-testid={`kpi-help-toggle-${tid}`}
        >
          <HelpCircle className="w-4 h-4" />
        </button>
      </div>

      {/* Banner: sin fórmula (solo corporate_admin) */}
      {!hasFormula && userRole === "corporate_admin" && (
        <div className="flex items-start gap-2 text-xs bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg px-3 py-2" data-testid={`kpi-no-formula-banner-${tid}`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Este KPI todavía no tiene fórmula configurada. Captura manual. Configura la fórmula para que el sistema calcule por el responsable.</span>
        </div>
      )}

      {/* Panel educativo */}
      {showHelp && (
        <div className="bg-slate-50 rounded-xl p-3 space-y-2.5 text-sm" data-testid={`kpi-help-panel-${tid}`}>
          {kpi.description ? (
            <p className="text-slate-700 whitespace-pre-line">{kpi.description}</p>
          ) : (
            <p className="text-slate-400 italic">Sin descripción configurada.</p>
          )}
          {kpi.formula_human && (
            <div>
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Fórmula</div>
              <div className="font-mono text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-700">{kpi.formula_human}</div>
            </div>
          )}
          {kpi.data_source && (
            <div className="flex items-start gap-2 text-xs text-slate-600">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
              <span><span className="font-semibold">Origen:</span> {kpi.data_source}</span>
            </div>
          )}
          {kpi.capture_example && (
            <div className="text-xs text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2">
              <span className="font-semibold">Ejemplo:</span> {kpi.capture_example}
            </div>
          )}
          {kpi.help_video_url && (
            <button
              onClick={() => setShowVideo(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800"
              data-testid={`kpi-video-btn-${tid}`}
            >
              <PlayCircle className="w-4 h-4" /> Ver video tutorial
            </button>
          )}
        </div>
      )}

      {/* Aviso: menor es mejor */}
      {hasFormula && kpi.score_direction === "lower_is_better" && (
        <div className="flex items-center gap-2 text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-3 py-2" data-testid={`kpi-lower-is-better-hint-${tid}`}>
          <ArrowDownCircle className="w-3.5 h-3.5 shrink-0" />
          <span>En este indicador, un valor menor a la meta es mejor.</span>
        </div>
      )}

      {/* Form de captura (o aviso de seleccionar sucursal para KPIs por sucursal) */}
      {readOnly ? (
        <div className="flex items-start gap-2 text-xs bg-slate-50 border border-slate-200 text-slate-600 rounded-lg px-3 py-2.5" data-testid={`kpi-readonly-banner-${tid}`}>
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>El resultado de este KPI lo captura RH. Aquí solo puedes consultarlo.</span>
        </div>
      ) : needsBranchSelection ? (
        <div className="flex items-start gap-2 text-xs bg-purple-50 border border-purple-200 text-purple-800 rounded-lg px-3 py-2.5" data-testid={`kpi-needs-branch-banner-${tid}`}>
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Este es un KPI por sucursal. Selecciona una sucursal arriba para verlo y capturarlo.</span>
        </div>
      ) : hasFormula ? (
        <div className="space-y-2.5">
          {inputDefs.map((f) => (
            <div key={f.key}>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                {f.label}{f.required !== false && <span className="text-red-500"> *</span>}
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="any"
                  value={inputs[f.key] ?? ""}
                  min={f.min ?? undefined}
                  max={f.max ?? undefined}
                  onChange={(e) => setInput(f.key, e.target.value)}
                  placeholder={f.help_text || ""}
                  className="w-full border border-slate-200 rounded-lg pl-3 pr-12 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] transition-shadow"
                  data-testid={`kpi-input-${tid}-${f.key}`}
                />
                {f.unit && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{f.unit}</span>}
              </div>
              {f.help_text && <p className="text-[11px] text-slate-400 mt-0.5">{f.help_text}</p>}
            </div>
          ))}

          {/* Preview en vivo */}
          <div className={`rounded-xl px-3 py-2.5 border ${rangeLevel ? RANGE_LEVEL_CLS[rangeLevel] : "bg-emerald-50 border-emerald-200"}`} data-testid={`kpi-preview-${tid}`}>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Valor calculado</div>
            <div className={`text-2xl font-extrabold ${rangeLevel ? RANGE_LEVEL_CLS[rangeLevel].split(" ").pop() : "text-emerald-700"}`} data-testid={`kpi-preview-value-${tid}`}>
              {formatOutput(calculated, kpi.output_unit, kpi.output_decimals)}
            </div>
            {rangeLevel && rangeLevel !== "verde" && (
              <p className="text-xs mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                {rangeLevel === "amarillo" ? "Cerca del rango aceptable." : "Está fuera del rango habitual"} ({kpi.acceptable_min}–{kpi.acceptable_max}). ¿Agregas una nota explicativa?
              </p>
            )}
          </div>
        </div>
      ) : (
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Valor capturado ({kpi.unit})</label>
          <input
            type="number"
            step="any"
            value={manualValue}
            onChange={(e) => setManualValue(e.target.value)}
            placeholder={`Valor en ${kpi.unit}`}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] transition-shadow"
            data-testid={`kpi-value-input-${tid}`}
          />
        </div>
      )}

      {/* Nota */}
      {!readOnly && !needsBranchSelection && (
        <div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Nota (opcional). Por ejemplo: 'Vendimos arriba de la meta porque hubo evento corporativo'."
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] resize-none"
            data-testid={`kpi-notes-input-${tid}`}
          />
        </div>
      )}

      {!readOnly && !needsBranchSelection && (
        <button
          onClick={handleSubmit}
          disabled={saving || !canSubmit}
          className="w-full py-3 rounded-xl bg-[#1A1A1A] font-semibold text-sm text-white hover:bg-black transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
          data-testid={`kpi-submit-btn-${tid}`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Guardar captura
        </button>
      )}

      {/* Histórico */}
      <div className="border-t border-slate-100 pt-2">
        <button
          onClick={toggleHistory}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700"
          data-testid={`kpi-history-toggle-${tid}`}
        >
          <HistoryIcon className="w-3.5 h-3.5" /> Histórico
          {showHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        {showHistory && (
          <div className="mt-2 space-y-2" data-testid={`kpi-history-${tid}`}>
            {!historyLoaded ? (
              <div className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Cargando…</div>
            ) : history.length === 0 ? (
              <p className="text-xs text-slate-400">Sin capturas previas.</p>
            ) : (
              <>
                <Sparkline values={[...history].reverse().map((r) => Number(r.value)).filter((v) => !Number.isNaN(v))} />
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-400 text-left">
                      <th className="font-medium py-1">Período</th>
                      <th className="font-medium py-1">Valor</th>
                      <th className="font-medium py-1">Quién</th>
                      <th className="font-medium py-1">Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="py-1 text-slate-600">{r.period}</td>
                        <td className="py-1 font-semibold text-slate-800">{formatOutput(r.value, r.output_unit || kpi.output_unit, kpi.output_decimals)}</td>
                        <td className="py-1 text-slate-500 truncate max-w-[80px]">{r.created_by_name || "—"}</td>
                        <td className="py-1 text-slate-400 truncate max-w-[120px]">{r.notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}
      </div>

      {/* Modal video */}
      {showVideo && kpi.help_video_url && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowVideo(false)}>
          <div className="bg-black rounded-xl w-full max-w-2xl aspect-video overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={kpi.help_video_url.replace("watch?v=", "embed/")}
              title="Video tutorial"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
}
